import { faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSelector } from "react-redux";
import ButtonAtom from "../button/button";
import "./action-column.scss";

/**
 * Checks whether an action should be visible for a given row.
 * Defaults to visible when `show` is omitted.
 */
const isActionVisible = (action, data, rowIndex) => {
	try {
		if (action.show) {
			return action.show(data, rowIndex);
		}
	} catch (err) {
		console.error("ActionColumn: visibility check failed", err);
		return false;
	}
	return true;
};

/**
 * Reusable action menu for AG Grid rows.
 * Renders a ⋯ button that opens a portalled dropdown, escaping AG Grid's
 * overflow/transform clipping.
 *
 * Action shape:
 *   { label, onClick, permission?, show?, danger? }
 *
 * `label` is a translation key or function(data) → key.
 * `show(data, rowIndex)` controls action visibility.
 * `danger: true` styles the item in red (auto-detected for keys containing "delete").
 */
const ActionColumn = ({ actions, data, rowIndex, t, context }) => {
	const { permissions: reduxPermissions } =
		useSelector((state) => state.auth?.user || {}) || {};

	const permissions =
		context?.permissions ||
		context?.userPermissions ||
		reduxPermissions ||
		[];

	const [open, setOpen] = useState(false);
	const [menuStyle, setMenuStyle] = useState({});
	const toggleRef = useRef(null);

	const hasPermission = (area, actionType) => {
		if (!area || !actionType) return true;
		if (Array.isArray(permissions)) {
			return permissions.some(
				(perm) => perm.area === area && perm.action === actionType,
			);
		}
		return !!(permissions?.[area]?.[actionType] === true);
	};

	const handleClose = useCallback(() => setOpen(false), []);

	useEffect(() => {
		if (!open) return;
		const onDown = (e) => {
			if (!toggleRef.current?.contains(e.target)) handleClose();
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("scroll", handleClose, true);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("scroll", handleClose, true);
		};
	}, [open, handleClose]);

	if (!data) return null;

	// `show` → hide entirely (business logic: action doesn't apply to this row)
	// `permission` → keep visible but disable (user may seek elevated access)
	const showableActions = actions.filter((action) =>
		isActionVisible(action, data, rowIndex),
	);

	if (showableActions.length === 0) return null;

	const projectCan = context?.projectCan;

	const enrichedActions = showableActions.map((action) => {
		const { area, action: actionType } = action.permission || {};
		const { area: projectArea, action: projectAction } =
			action.projectPermission || {};

		let permissionDenied = false;
		if (projectArea && projectAction && projectCan) {
			permissionDenied = !projectCan(projectArea, projectAction);
		} else if (area && actionType) {
			permissionDenied = !hasPermission(area, actionType);
		}

		// Data-based disable check (takes lower priority than permission)
		const dataDisabled = !permissionDenied && action.disabled
			? typeof action.disabled === "function"
				? action.disabled(data, rowIndex)
				: !!action.disabled
			: false;

		let disabledTooltip = null;
		if (permissionDenied) {
			disabledTooltip = "common.no-permission";
		} else if (dataDisabled) {
			const tip = action.disabledTooltip;
			disabledTooltip = tip
				? typeof tip === "function"
					? tip(data, rowIndex)
					: tip
				: "common.action-not-allowed";
		}

		return { ...action, disabled: permissionDenied || dataDisabled, disabledTooltip };
	});

	const handleToggle = (e) => {
		e.stopPropagation();
		if (open) {
			setOpen(false);
			return;
		}

		const rect = toggleRef.current.getBoundingClientRect();
		const menuWidth = 170;
		const menuHeight = enrichedActions.length * 38 + 8;
		const top =
			window.innerHeight - rect.bottom < menuHeight
				? rect.top - menuHeight - 4
				: rect.bottom + 4;

		setMenuStyle({
			position: "fixed",
			top,
			left: Math.max(4, rect.right - menuWidth),
			minWidth: menuWidth,
			zIndex: 9999,
		});
		setOpen(true);
	};

	const menu = open
		? ReactDOM.createPortal(
			<ul className="action-column-menu" style={menuStyle}>
				{enrichedActions.map((action, index) => {
					const rawLabel = action.label;
					const labelKey =
						typeof rawLabel === "function"
							? rawLabel(data, rowIndex)
							: rawLabel;
					const isDanger =
						action.danger ??
						(typeof labelKey === "string" &&
							labelKey.toLowerCase().includes("delete"));

					const className = `action-menu-item${isDanger ? " action-item--danger" : ""}${action.disabled ? " action-item--disabled" : ""}`;

					if (action.disabled) {
						return (
							<OverlayTrigger
								key={index}
								placement="left"
								overlay={<Tooltip id={`action-disabled-${index}`}>{t(action.disabledTooltip)}</Tooltip>}
							>
								<li className={className}>{t(labelKey)}</li>
							</OverlayTrigger>
						);
					}

					return (
						<li
							key={index}
							className={className}
							onMouseDown={(e) => {
								e.stopPropagation();
								setOpen(false);
								action.onClick(data, rowIndex, e);
							}}
						>
							{t(labelKey)}
						</li>
					);
				})}
			</ul>,
			document.body,
		)
		: null;

	return (
		<div className="action-column-dropdown">
			<ButtonAtom
				ref={toggleRef}
				className="action-column-toggle"
				onClick={handleToggle}
			>
				<FontAwesomeIcon icon={faEllipsis} />
			</ButtonAtom>
			{menu}
		</div>
	);
};

export default ActionColumn;
